import { useState, useEffect } from 'react';
import api from '../../utils/api';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../ui/select';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../ui/dialog';
import { toast } from 'sonner';
import { Receipt, ArrowClockwise, MagnifyingGlass } from '@phosphor-icons/react';

const PAYMENT_MODES = ['Cash', 'Card', 'Cheque', 'UPI', 'NEFT', 'RTGS', 'IMPS', 'Bank Transfer', 'E-Transfer', 'Other'];

const fmtINR = (n) => '₹ ' + Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });

/**
 * Reusable Receipt Dialog. Opens against a quotation_no OR jo_number and
 * auto-fetches the customer/mobile/site/executive/value/received/balance
 * context. Falls back gracefully if no context (e.g. pure advance receipt).
 *
 * Props:
 *  - open: boolean
 *  - onOpenChange: fn(open)
 *  - quotationNo?: string
 *  - joNumber?: string
 *  - prefill?: { customer_name, customer_mobile, site_location, executive_name, order_value }
 *  - defaultReceiptType?: 'quotation' | 'advance' | 'adjustment'
 *  - onCreated?: fn(receipt) — callback after successful creation
 */
export function ReceiptDialog({
  open, onOpenChange, quotationNo, joNumber, prefill = {},
  defaultReceiptType = 'quotation', onCreated,
}) {
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  // Lookup state — used when the dialog is opened without a quotationNo
  const [lookupQ, setLookupQ] = useState('');
  const [lookupM, setLookupM] = useState('');
  const [lookupLoading, setLookupLoading] = useState(false);
  const [quotationOptions, setQuotationOptions] = useState(null); // list from mobile lookup
  const [pickedQno, setPickedQno] = useState(null);
  const [form, setForm] = useState({
    receipt_type: defaultReceiptType,
    amount: '',
    payment_date: new Date().toISOString().slice(0, 10),
    payment_mode: 'Cash',
    received_bank: '',
    reference: '',
    remarks: '',
    customer_name: prefill.customer_name || '',
    customer_mobile: prefill.customer_mobile || '',
    site_location: prefill.site_location || '',
    executive_name: prefill.executive_name || '',
  });

  useEffect(() => {
    if (!open) return;
    // Reset form each open
    setForm((f) => ({
      ...f,
      receipt_type: defaultReceiptType,
      amount: '',
      payment_date: new Date().toISOString().slice(0, 10),
      payment_mode: 'Cash',
      received_bank: '',
      reference: '',
      remarks: '',
      customer_name: prefill.customer_name || '',
      customer_mobile: prefill.customer_mobile || '',
      site_location: prefill.site_location || '',
      executive_name: prefill.executive_name || '',
    }));
    setLookupQ('');
    setLookupM('');
    setQuotationOptions(null);
    setPickedQno(null);
    if (!quotationNo) { setCtx(null); return; }
    setLoading(true);
    api.get(`/api/admin/quotations/${encodeURIComponent(quotationNo)}/receipt-context`)
      .then((r) => {
        setCtx(r.data);
        setForm((f) => ({
          ...f,
          customer_name: r.data.customer_name || f.customer_name,
          customer_mobile: r.data.customer_mobile || f.customer_mobile,
          site_location: r.data.site_location || f.site_location,
          executive_name: r.data.executive_name || f.executive_name,
        }));
      })
      .catch((e) => toast.error(e.response?.data?.detail || 'Failed to load quotation context'))
      .finally(() => setLoading(false));
  }, [open, quotationNo]);

  const activeQno = quotationNo || pickedQno;

  const runLookup = async () => {
    const params = {};
    if (lookupQ.trim()) params.quotation_no = lookupQ.trim();
    else if (lookupM.trim()) params.customer_mobile = lookupM.trim();
    else { toast.error('Enter a Quotation No or Mobile'); return; }
    setLookupLoading(true);
    try {
      const { data } = await api.get('/api/admin/receipts/lookup', { params });
      // Two response shapes: single (has order_value at top) OR mobile list (has quotations array)
      if (data.quotations && Array.isArray(data.quotations)) {
        // Mobile lookup
        if (data.quotations.length === 0) {
          toast.info('No quotations found for this mobile — enter as advance below');
          setCtx(null);
          setForm((f) => ({
            ...f,
            customer_mobile: data.customer_mobile || lookupM,
            customer_name: data.customer_name || '',
            site_location: data.site_location || '',
            executive_name: data.executive_name || '',
            receipt_type: 'advance',
          }));
          setQuotationOptions([]);
          return;
        }
        if (data.quotations.length === 1) {
          const q = data.quotations[0];
          applyQuotation(q, data);
          return;
        }
        // Multi — let the user pick
        setQuotationOptions(data.quotations);
        setForm((f) => ({
          ...f,
          customer_mobile: data.customer_mobile,
          customer_name: data.customer_name,
          site_location: data.site_location,
          executive_name: data.executive_name,
        }));
      } else {
        // Single quotation shape
        setCtx(data);
        setPickedQno(data.quotation_no);
        setForm((f) => ({
          ...f,
          customer_name: data.customer_name,
          customer_mobile: data.customer_mobile,
          site_location: data.site_location,
          executive_name: data.executive_name,
          receipt_type: 'quotation',
        }));
        setQuotationOptions(null);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Lookup failed');
    } finally {
      setLookupLoading(false);
    }
  };

  const applyQuotation = (q, mobileData = null) => {
    setPickedQno(q.quotation_no);
    setCtx({
      quotation_no: q.quotation_no,
      jo_number: q.jo_number,
      customer_name: q.customer_name || mobileData?.customer_name || '',
      customer_mobile: mobileData?.customer_mobile || form.customer_mobile,
      site_location: q.site_location || mobileData?.site_location || '',
      executive_name: q.executive_name || mobileData?.executive_name || '',
      order_value: q.order_value,
      total_received: q.total_received,
      balance: q.balance,
    });
    setForm((f) => ({
      ...f,
      customer_name: q.customer_name || mobileData?.customer_name || f.customer_name,
      customer_mobile: mobileData?.customer_mobile || f.customer_mobile,
      site_location: q.site_location || mobileData?.site_location || f.site_location,
      executive_name: q.executive_name || mobileData?.executive_name || f.executive_name,
      receipt_type: 'quotation',
    }));
    setQuotationOptions(null);
  };

  const resetLookup = () => {
    setCtx(null);
    setPickedQno(null);
    setQuotationOptions(null);
    setLookupQ('');
    setLookupM('');
  };

  const save = async () => {
    const amt = parseFloat(form.amount);
    if (!amt || amt <= 0) { toast.error('Enter a valid amount'); return; }
    if (!form.customer_mobile || form.customer_mobile.replace(/\D/g, '').length < 10) {
      toast.error('Valid 10-digit customer mobile required'); return;
    }
    if (!form.payment_date) { toast.error('Receipt date required'); return; }

    setSaving(true);
    try {
      const payload = {
        customer_mobile: form.customer_mobile,
        customer_name: form.customer_name,
        site_location: form.site_location,
        executive_name: form.executive_name,
        quotation_no: activeQno || null,
        jo_number: joNumber || ctx?.jo_number || null,
        receipt_type: form.receipt_type,
        amount: amt,
        payment_date: form.payment_date,
        payment_mode: form.payment_mode,
        received_bank: form.received_bank,
        reference: form.reference,
        remarks: form.remarks,
      };
      const { data } = await api.post('/api/admin/receipts', payload);
      toast.success(`Receipt ${data.receipt_number} saved`);
      onOpenChange(false);
      onCreated && onCreated(data.receipt);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Save failed');
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[92vh] overflow-y-auto" data-testid="receipt-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt size={22} className="text-orange-500" />
            New Receipt {activeQno ? <span className="text-sm text-zinc-500 font-normal font-mono">against {activeQno}</span> : null}
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-zinc-400 text-sm">Loading quotation context…</div>
        ) : (
          <div className="space-y-4">
            {/* Lookup panel — shown only when the dialog was opened without a quotation prop */}
            {!quotationNo && !ctx && (
              <div className="p-3 rounded-sm bg-orange-50 border border-orange-200 space-y-2" data-testid="receipt-lookup-panel">
                <div className="text-xs uppercase tracking-wider text-orange-900 font-bold flex items-center gap-1">
                  <MagnifyingGlass size={14} /> Look up customer / quotation
                </div>
                <div className="grid grid-cols-1 md:grid-cols-5 gap-2">
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">Quotation No</Label>
                    <Input value={lookupQ}
                      onChange={(e) => { setLookupQ(e.target.value); if (e.target.value) setLookupM(''); }}
                      placeholder="e.g. QW-1234"
                      data-testid="receipt-lookup-quotation"
                      onKeyDown={(e) => e.key === 'Enter' && runLookup()} />
                  </div>
                  <div className="md:col-span-2">
                    <Label className="text-[10px] uppercase tracking-wider text-zinc-500">OR Mobile No</Label>
                    <Input value={lookupM}
                      onChange={(e) => { setLookupM(e.target.value); if (e.target.value) setLookupQ(''); }}
                      placeholder="10-digit"
                      data-testid="receipt-lookup-mobile"
                      onKeyDown={(e) => e.key === 'Enter' && runLookup()} />
                  </div>
                  <div className="flex items-end">
                    <Button onClick={runLookup} disabled={lookupLoading}
                      className="w-full bg-orange-500 hover:bg-orange-600 text-white"
                      data-testid="receipt-lookup-btn">
                      {lookupLoading ? <ArrowClockwise size={14} className="animate-spin" /> : <MagnifyingGlass size={14} className="mr-1" />}
                      {lookupLoading ? 'Looking…' : 'Fetch'}
                    </Button>
                  </div>
                </div>
                <div className="text-[10px] text-zinc-500">
                  Enter a Quotation No or Mobile — we&apos;ll auto-pull customer name, site, executive, order value, already received &amp; balance.
                  <br />No quotation? Just enter the mobile — we&apos;ll record the payment as an advance.
                </div>
              </div>
            )}

            {/* Multi-quotation picker */}
            {!ctx && quotationOptions && quotationOptions.length > 0 && (
              <div className="p-3 rounded-sm bg-blue-50 border border-blue-200">
                <div className="text-xs uppercase tracking-wider text-blue-900 font-bold mb-2">
                  {quotationOptions.length} quotations found — pick one
                </div>
                <div className="space-y-1 max-h-56 overflow-y-auto">
                  {quotationOptions.map((q) => (
                    <button key={q.quotation_no}
                      onClick={() => applyQuotation(q)}
                      data-testid={`receipt-pick-${q.quotation_no}`}
                      className="w-full flex items-center justify-between p-2 rounded-sm bg-white hover:bg-blue-100 border border-blue-100 text-left transition-colors">
                      <div>
                        <div className="font-mono text-xs font-bold text-blue-800">{q.quotation_no}</div>
                        <div className="text-[10px] text-zinc-500">{q.customer_name} · {q.site_location || '—'}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-xs">Order: <span className="font-bold">{fmtINR(q.order_value)}</span></div>
                        <div className="text-[10px] text-red-700">Balance: <span className="font-bold">{fmtINR(q.balance)}</span></div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Auto-fetched Context Panel */}
            {ctx && (
              <div className="relative p-3 rounded-sm bg-zinc-50 border border-zinc-200">
                {!quotationNo && (
                  <Button size="sm" variant="ghost" onClick={resetLookup}
                    className="absolute top-1 right-1 text-xs h-6"
                    data-testid="receipt-change-quotation">
                    Change
                  </Button>
                )}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  <ContextCell label="Customer" value={ctx.customer_name} />
                  <ContextCell label="Mobile" value={ctx.customer_mobile} mono />
                  <ContextCell label="Site" value={ctx.site_location} />
                  <ContextCell label="Executive" value={ctx.executive_name} />
                  <ContextCell label="Quotation Value" value={fmtINR(ctx.order_value)} accent="text-zinc-900 font-bold" />
                  <ContextCell label="Already Received" value={fmtINR(ctx.total_received)} accent="text-blue-700 font-bold" />
                  <ContextCell label="Balance" value={fmtINR(ctx.balance)} accent="text-red-700 font-bold" data-testid="receipt-balance" />
                  <ContextCell label="JO Number" value={ctx.jo_number || '—'} mono />
                </div>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Receipt Type</Label>
                <Select value={form.receipt_type} onValueChange={(v) => setForm({ ...form, receipt_type: v })}>
                  <SelectTrigger data-testid="receipt-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="quotation">Against Quotation</SelectItem>
                    <SelectItem value="advance">Customer Advance</SelectItem>
                    <SelectItem value="adjustment">Adjustment</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Amount Received*</Label>
                <Input type="number" min="0" value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  data-testid="receipt-amount"
                  placeholder="0.00" />
                {ctx && form.amount && parseFloat(form.amount) > ctx.balance + 0.005 && (
                  <div className="text-[10px] text-amber-700 mt-1">⚠ exceeds current balance of {fmtINR(ctx.balance)}</div>
                )}
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Receipt Date*</Label>
                <Input type="date" value={form.payment_date}
                  onChange={(e) => setForm({ ...form, payment_date: e.target.value })}
                  data-testid="receipt-date" />
              </div>

              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Payment Mode</Label>
                <Select value={form.payment_mode} onValueChange={(v) => setForm({ ...form, payment_mode: v })}>
                  <SelectTrigger data-testid="receipt-mode"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {PAYMENT_MODES.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Received Bank</Label>
                <Input value={form.received_bank}
                  onChange={(e) => setForm({ ...form, received_bank: e.target.value })}
                  data-testid="receipt-bank"
                  placeholder="e.g. HDFC / ICICI" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-wider text-zinc-500">Reference / UTR / Cheque</Label>
                <Input value={form.reference}
                  onChange={(e) => setForm({ ...form, reference: e.target.value })}
                  data-testid="receipt-ref"
                  placeholder="UTR / Cheque # / Txn ID" />
              </div>
            </div>

            {!ctx && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3 border-t border-zinc-200 pt-3">
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Customer Name*</Label>
                  <Input value={form.customer_name}
                    onChange={(e) => setForm({ ...form, customer_name: e.target.value })}
                    data-testid="receipt-customer-name" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Customer Mobile*</Label>
                  <Input value={form.customer_mobile}
                    onChange={(e) => setForm({ ...form, customer_mobile: e.target.value })}
                    data-testid="receipt-customer-mobile" />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Site Location</Label>
                  <Input value={form.site_location}
                    onChange={(e) => setForm({ ...form, site_location: e.target.value })} />
                </div>
                <div>
                  <Label className="text-xs uppercase tracking-wider text-zinc-500">Executive</Label>
                  <Input value={form.executive_name}
                    onChange={(e) => setForm({ ...form, executive_name: e.target.value })} />
                </div>
              </div>
            )}

            <div>
              <Label className="text-xs uppercase tracking-wider text-zinc-500">Remarks</Label>
              <Textarea rows={2} value={form.remarks}
                onChange={(e) => setForm({ ...form, remarks: e.target.value })}
                data-testid="receipt-remarks" />
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={save} disabled={saving}
            className="bg-orange-500 hover:bg-orange-600 text-white"
            data-testid="receipt-save">
            {saving ? <ArrowClockwise size={14} className="mr-1 animate-spin" /> : <Receipt size={14} className="mr-1" />}
            {saving ? 'Saving…' : 'Save Receipt'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ContextCell({ label, value, mono, accent, ...rest }) {
  return (
    <div {...rest}>
      <div className="text-[10px] uppercase tracking-wider text-zinc-500">{label}</div>
      <div className={`text-sm mt-0.5 ${accent || 'text-zinc-800'} ${mono ? 'font-mono' : ''}`}>{value || '—'}</div>
    </div>
  );
}

// Badge helper for receipt source
export function ReceiptSourceBadge({ source }) {
  const s = String(source || '').toLowerCase();
  const cls = s === 'razorpay' ? 'bg-blue-100 text-blue-800 hover:bg-blue-100' :
              'bg-emerald-100 text-emerald-800 hover:bg-emerald-100';
  return <Badge className={`${cls} text-[10px]`}>{s === 'razorpay' ? 'AUTO' : 'MANUAL'}</Badge>;
}
